import { TableDetailsDto, TableDetailsFidelityDto } from "../../services/tables/dto/table-details-dto";
import { CloseType, ReceiptType } from "../services/table.service";
import { Installation } from "./installation";
import { PaymentDone } from "./payment-done";
import { TableItem } from "./table-item";
import { PaymentDiscount } from "./payment-discount";
import { OpenedCart } from "./produto";

export class Table {
  id: number;
  idi: number;
  tableId: number;
  tableCartId: number;
  tableSession: string;
  total: number;
  discount: number;
  totalItens: number;
  tax: number;
  paid: number;
  paidValue: number = 0;
  taxPaid: boolean;
  status: string;
  items: TableItem[];
  installation: Installation;
  removeTax?: boolean;
  paymentType?: CloseType = 'TOTAL';
  receiptType?: ReceiptType = 'ALL';
  customPayment?: number = 0;
  payments: PaymentDone[];
  tipType?: 'fixed' | 'percent'
  /** valor numérico da taxa de serviço configurada (product_tip.tax_type) — % quando tipType='percent', R$ quando 'fixed'. */
  tipValue?: number
  proportionalTax?: number
  moduleBot?: boolean
  modulePaymentTable?: boolean
  paymentDiscount?: PaymentDiscount
  tableName?: string;
  moduloMaiorValorComplemento?: boolean
  openedCart?: OpenedCart;
  locked?: number;
  nome?: string;
  descricao?: string;
  vouchers?: any[];
  automaticIncrease?: number;
  fidelity?: TableDetailsFidelityDto;
  /** valor restante a pagar na mesa (fonte: back, NUNCA recalcular no front). */
  remaining: number;
  /** soma dos itens consumidos, exclui a "Taxa de serviço". Fonte: back. */
  subtotal: number;
  /** valor da mesa após desconto/cupom/acréscimo/taxa-de-serviço, sem subtrair paid. Fonte: back. */
  tableFinalValue: number;
  /** valor da "Taxa de serviço". Fonte: back. */
  serviceFeeValue: number;
  /** codigo do cliente dono da mesa (fixado no 1º pedido). 0 quando não há dono. Só ele ganha/resgata cashback. */
  officialClientId: number;
  /**
   * cashback aplicado na mesa (user_table_cashback.cashback_value). Fonte: back.
   * Visível a todos os clientes da mesa. remaining/tableFinalValue já vêm abatidos
   * — use este campo só pra exibição na linha "Cashback" do resumo.
   */
  cashbackApplied: number;

  constructor(tableDetailsDto: TableDetailsDto) {
    this.id = tableDetailsDto.id;
    this.idi = tableDetailsDto.idi;
    this.tableId = tableDetailsDto.tableId;
    this.tableCartId = tableDetailsDto.tableCartId;
    this.tableSession = tableDetailsDto.tableSession;
    this.total = tableDetailsDto.total;
    this.discount = tableDetailsDto.discount;
    this.totalItens = tableDetailsDto.totalItens;
    this.tax = tableDetailsDto.tax;
    this.paid = tableDetailsDto.paid;
    this.taxPaid = tableDetailsDto.taxPaid;
    this.status = tableDetailsDto.status;
    this.items = [];
    this.removeTax = this.taxPaid ?? false;
    this.tipType = tableDetailsDto.tipType;
    this.tipValue = tableDetailsDto.tipValue ?? 0;
    this.moduleBot = tableDetailsDto.moduleBot ?? false;
    this.modulePaymentTable = tableDetailsDto.modulePaymentTable ?? false;
    this.moduloMaiorValorComplemento = tableDetailsDto.moduloMaiorValorComplemento ?? false;
    const paymentDiscountsDto = tableDetailsDto.paymentDiscounts;
    this.paymentDiscount = paymentDiscountsDto && paymentDiscountsDto.discountValue > 0
      ? new PaymentDiscount(paymentDiscountsDto)
      : undefined;
    this.tableName = tableDetailsDto.tableName;
    this.fidelity = tableDetailsDto.fidelity || undefined;
    this.remaining = tableDetailsDto.remaining ?? 0;
    this.subtotal = tableDetailsDto.subtotal ?? 0;
    this.tableFinalValue = tableDetailsDto.tableFinalValue ?? 0;
    this.serviceFeeValue = tableDetailsDto.serviceFeeValue ?? 0;
    this.officialClientId = tableDetailsDto.officialClientId ?? 0;
    this.cashbackApplied = tableDetailsDto.cashbackApplied ?? 0;

    for(let item of tableDetailsDto.items) {
      this.items.push(new TableItem(item))
    }

    this.installation = new Installation(tableDetailsDto.installation);
    this.payments = [];
    this.vouchers = tableDetailsDto.vouchers;
    this.automaticIncrease = tableDetailsDto.automaticIncrease ?? 0;

    if(tableDetailsDto.payments) {
      for(let payment of tableDetailsDto.payments) {
        this.payments.push(new PaymentDone(payment, this.installation.timezone))
        this.paidValue += payment.value;
      }
    }
  }

  get totalWithTax() {
    if(this.taxPaid || this.removeTax) {
      return this.total
    } else {
      return this.total + this.tax;
    }
  }

  get voucherDiscount(): number {
    if (!this.vouchers || this.vouchers.length === 0) return 0;
    const voucher = this.vouchers[0];
    if (voucher.discountValueApplied) return Number(voucher.discountValueApplied);
    return 0;
  }

  /** @deprecated use `remaining` (vem do back). Kept for callers que pagam o total da mesa. */
  get totalToPay() {
    return this.remaining;
  }

  get totalItensSelected() {
    const totalItensSelectedWithoutTip = this.getTotalItensSelectedWithoutTip()
    let totalSelectedItens = totalItensSelectedWithoutTip;

    if(this.paymentType == 'PARTIAL') {
      const tip = this.getTip()
      const serviceItemsValue = this.getServiceItemsValue();

      if(tip) {
        const proportion = totalItensSelectedWithoutTip / (this.total - tip.value - serviceItemsValue);
        totalSelectedItens += tip.value * proportion
      }
      if(this.tax && (!this.taxPaid || !this.removeTax)) {
        const proportion = totalItensSelectedWithoutTip / (this.total - (tip?.value || 0) - serviceItemsValue);
        this.proportionalTax = parseFloat((this.tax * proportion).toFixed(2))
        totalSelectedItens += this.proportionalTax;
      }
    } else {
      if(this.taxPaid || this.removeTax) {
        totalSelectedItens += this.tax;
      }
    }

    return parseFloat(totalSelectedItens.toFixed(2));
  }

  getTotalItensSelectedWithoutTip(): number {
    let total = 0;

    for(let item of this.items) {
      if(!item.tip){
        total += item.totalSelected
      }
    }

    return total;
  }

  getTotalWithoutServiceItems(): number {
    let total = 0;

    for(let item of this.items) {
      if(!item.isService()){
        total += item.value * item.qty;
      }
    }

    return total;
  }

  getTotalSelectedWithoutServiceAndTip(): number {
    let total = 0;

    for(let item of this.items) {
      if(!item.tip && !item.isService()){
        total += item.totalSelected;
      }
    }

    return total;
  }

  getProportionalValue(value: number, total: number): number{
    const serviceItemsValue = this.getServiceItemsValue();
    return ((total / (this.total - (this.getTip()?.value || 0) - serviceItemsValue)) * value);
  }

  getServiceItemsValue(): number {
    let total = 0;
    for(let item of this.items) {
      if(item.isService()){
        total += item.value * item.qty;
      }
    }
    return total;
  }

  get discountPercent() {
    if (this.tax) {
      const fixedValue = Number((this.tax / this.total * 100).toFixed(2));
      return Math.round(fixedValue);
    } else {
      return 0;
    }
  }

  get qtyItensSelected() {
    let qty = 0;
    for(let item of this.items) {
      qty += item.qtySelected
    }

    return qty;
  }

  /** @deprecated use `remaining` (vem do back, fonte da verdade). */
  get remainingValue() {
    return this.remaining;
  }

  get tableAlreadyPaid() {
    return this.total <= this.paidValue;
  }

  get totalUserPaid(): number {
    if(this.paymentType == 'TOTAL') {
      return this.totalToPay;
    } else if (this.paymentType == 'PARTIAL') {
      return this.totalItensSelected;
    } else {
      return this.customPayment ?? 0;
    }
  }

  setPaymentType(type: CloseType) {
    this.paymentType = type;
  }

  setReceiptType(type: ReceiptType) {
    this.receiptType = type;
  }

  getTip(): TableItem | undefined {
    return this.items.find((item: TableItem) => item.tip);
  }
  getPaymentDiscount(): PaymentDiscount | undefined {
    return this.paymentDiscount;
  }
}
